import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { MinusIcon, PlusIcon, ScanIcon, XIcon } from "lucide-react";
import { i18n } from "../../i18n";
import { Button } from "../ui/button";
import { DialogClose, DialogContent, DialogTitle } from "../ui/dialog";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { MermaidDiagram, type MermaidRenderResult } from "./MermaidDiagram";
import {
  fitMermaidTransform,
  mermaidScaleBounds,
  mermaidViewBoxSize,
  panMermaidTransform,
  pinchMermaidTransform,
  zoomMermaidTransform,
  type MermaidPoint,
  type MermaidSize,
  type MermaidTransform,
} from "./mermaidViewport";

interface MermaidDiagramViewerProps {
  code: string;
  theme: "light" | "dark";
}

interface MermaidPanZoomViewProps {
  children: ReactNode;
  closeAction?: ReactNode;
  contentSize: MermaidSize | null;
}

interface ActivePointer {
  point: MermaidPoint;
  type: string;
}

const IDENTITY_TRANSFORM: MermaidTransform = { scale: 1, x: 0, y: 0 };
const ZOOM_STEP = 1.25;

export function MermaidPanZoomView({
  children,
  closeAction,
  contentSize,
}: MermaidPanZoomViewProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const liveTransformRef = useRef(IDENTITY_TRANSFORM);
  const fitTransformRef = useRef<MermaidTransform | null>(null);
  const pendingTransformRef = useRef<MermaidTransform | null>(null);
  const frameRef = useRef<number | null>(null);
  const activePointersRef = useRef(new Map<number, ActivePointer>());
  const hasInteractedRef = useRef(false);
  const [transform, setTransform] = useState(IDENTITY_TRANSFORM);

  const commitTransform = useCallback((next: MermaidTransform) => {
    liveTransformRef.current = next;
    pendingTransformRef.current = null;
    if (frameRef.current != null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = null;
    setTransform(next);
  }, []);

  const scheduleTransform = useCallback((next: MermaidTransform) => {
    liveTransformRef.current = next;
    pendingTransformRef.current = next;
    if (frameRef.current != null) return;
    if (typeof requestAnimationFrame !== "function") {
      pendingTransformRef.current = null;
      setTransform(next);
      return;
    }
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const pending = pendingTransformRef.current;
      pendingTransformRef.current = null;
      if (pending) setTransform(pending);
    });
  }, []);

  const fitToViewport = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport || !contentSize || contentSize.width <= 0 || contentSize.height <= 0) return;
    const next = fitMermaidTransform(
      { width: viewport.clientWidth, height: viewport.clientHeight },
      contentSize,
    );
    fitTransformRef.current = next;
    hasInteractedRef.current = false;
    commitTransform(next);
  }, [commitTransform, contentSize]);

  useLayoutEffect(() => {
    fitToViewport();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (!hasInteractedRef.current) fitToViewport();
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [fitToViewport]);

  useEffect(
    () => () => {
      activePointersRef.current.clear();
      if (frameRef.current != null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(frameRef.current);
      }
    },
    [],
  );

  const updateScale = useCallback(
    (requestedScale: number, anchor: MermaidPoint) => {
      const fit = fitTransformRef.current;
      if (!fit) return;
      hasInteractedRef.current = true;
      scheduleTransform(
        zoomMermaidTransform(
          liveTransformRef.current,
          requestedScale,
          anchor,
          mermaidScaleBounds(fit.scale),
        ),
      );
    },
    [scheduleTransform],
  );

  const zoomFromCenter = useCallback(
    (factor: number) => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      updateScale(liveTransformRef.current.scale * factor, {
        x: viewport.clientWidth / 2,
        y: viewport.clientHeight / 2,
      });
    },
    [updateScale],
  );

  const pointInViewport = useCallback(
    (event: Pick<ReactPointerEvent<HTMLDivElement>, "clientX" | "clientY" | "currentTarget">) => {
      const rect = event.currentTarget.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    },
    [],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && (event.button !== 0 || !event.isPrimary)) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      activePointersRef.current.set(event.pointerId, {
        point: pointInViewport(event),
        type: event.pointerType,
      });
    },
    [pointInViewport],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const active = activePointersRef.current.get(event.pointerId);
      if (!active) return;
      if (active.type === "mouse" && event.buttons === 0) {
        activePointersRef.current.delete(event.pointerId);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        return;
      }

      event.preventDefault();
      const previousTouches = touchPair(activePointersRef.current);
      const nextPoint = pointInViewport(event);
      activePointersRef.current.set(event.pointerId, { ...active, point: nextPoint });
      const nextTouches = touchPair(activePointersRef.current);
      const fit = fitTransformRef.current;
      if (!fit) return;

      hasInteractedRef.current = true;
      if (previousTouches && nextTouches) {
        scheduleTransform(
          pinchMermaidTransform(
            liveTransformRef.current,
            previousTouches,
            nextTouches,
            mermaidScaleBounds(fit.scale),
          ),
        );
        return;
      }
      scheduleTransform(
        panMermaidTransform(liveTransformRef.current, {
          x: nextPoint.x - active.point.x,
          y: nextPoint.y - active.point.y,
        }),
      );
    },
    [pointInViewport, scheduleTransform],
  );

  const releasePointer = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    activePointersRef.current.delete(event.pointerId);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const cancelPointer = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    activePointersRef.current.delete(event.pointerId);
  }, []);

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      updateScale(liveTransformRef.current.scale * Math.exp(-event.deltaY * 0.01), {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    },
    [updateScale],
  );

  const fit = fitTransformRef.current;
  const bounds = fit ? mermaidScaleBounds(fit.scale) : null;
  const controlsDisabled = contentSize == null || fit == null;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b bg-muted/60 px-2 py-1.5">
        <span className="min-w-0 truncate px-2 font-medium text-sm">
          {i18n.t("markdown.mermaid.viewerTitle")}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <ViewerControl
            disabled={controlsDisabled || (bounds != null && transform.scale <= bounds.min)}
            label={i18n.t("markdown.mermaid.zoomOut")}
            onClick={() => zoomFromCenter(1 / ZOOM_STEP)}
          >
            <MinusIcon className="size-4" />
          </ViewerControl>
          <span className="w-12 text-center font-mono text-muted-foreground text-xs tabular-nums">
            {Math.round(transform.scale * 100)}%
          </span>
          <ViewerControl
            disabled={controlsDisabled || (bounds != null && transform.scale >= bounds.max)}
            label={i18n.t("markdown.mermaid.zoomIn")}
            onClick={() => zoomFromCenter(ZOOM_STEP)}
          >
            <PlusIcon className="size-4" />
          </ViewerControl>
          <ViewerControl
            disabled={controlsDisabled}
            label={i18n.t("markdown.mermaid.fit")}
            onClick={fitToViewport}
          >
            <ScanIcon className="size-4" />
          </ViewerControl>
          {closeAction}
        </div>
      </div>
      <div
        ref={viewportRef}
        className="relative min-h-0 flex-1 touch-none cursor-grab overflow-hidden bg-background select-none active:cursor-grabbing [background-image:radial-gradient(color-mix(in_srgb,var(--border)_55%,transparent)_1px,transparent_1px)] [background-size:16px_16px]"
        data-mermaid-viewport=""
        onLostPointerCapture={cancelPointer}
        onPointerCancel={cancelPointer}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={releasePointer}
        onWheel={handleWheel}
      >
        {contentSize ? (
          <div
            className="absolute left-0 top-0 origin-top-left will-change-transform"
            data-mermaid-transform=""
            data-scale={transform.scale}
            data-x={transform.x}
            data-y={transform.y}
            style={{
              height: contentSize.height,
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
              width: contentSize.width,
            }}
          >
            {children}
          </div>
        ) : (
          <div className="absolute inset-0 grid place-items-center">{children}</div>
        )}
      </div>
    </div>
  );
}

export function MermaidDiagramViewer({ code, theme }: MermaidDiagramViewerProps) {
  const [contentSize, setContentSize] = useState<MermaidSize | null>(null);
  const handleRenderStateChange = useCallback((result: MermaidRenderResult) => {
    setContentSize(result.status === "ready" ? mermaidViewBoxSize(result.svg) : null);
  }, []);

  return (
    <DialogContent
      bottomStickOnMobile={false}
      className="h-[88vh] w-[92vw] max-w-none overflow-hidden p-0 max-sm:h-dvh max-sm:w-screen max-sm:rounded-none max-sm:border-0"
      showCloseButton={false}
      viewportClassName="max-sm:block max-sm:p-0"
    >
      <DialogTitle className="sr-only">{i18n.t("markdown.mermaid.viewerTitle")}</DialogTitle>
      <MermaidPanZoomView
        contentSize={contentSize}
        closeAction={
          <Tooltip>
            <TooltipTrigger
              render={
                <DialogClose
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={i18n.t("common.close")}
                    />
                  }
                />
              }
            >
              <XIcon className="size-4" />
            </TooltipTrigger>
            <TooltipPopup side="bottom">{i18n.t("common.close")}</TooltipPopup>
          </Tooltip>
        }
      >
        <div className="chat-markdown grid h-full w-full place-items-center">
          <MermaidDiagram
            className="!h-full !w-full !overflow-visible !p-0 [&_svg]:!h-full [&_svg]:!w-full [&_svg]:!max-w-none"
            code={code}
            fallback={null}
            onRenderStateChange={handleRenderStateChange}
            theme={theme}
          />
        </div>
      </MermaidPanZoomView>
    </DialogContent>
  );
}

function ViewerControl({
  children,
  disabled,
  label,
  onClick,
}: {
  children: ReactNode;
  disabled: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={disabled}
            onClick={onClick}
            aria-label={label}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipPopup side="bottom">{label}</TooltipPopup>
    </Tooltip>
  );
}

function touchPair(
  pointers: ReadonlyMap<number, ActivePointer>,
): readonly [MermaidPoint, MermaidPoint] | null {
  const touches: MermaidPoint[] = [];
  for (const pointer of pointers.values()) {
    if (pointer.type === "touch") touches.push(pointer.point);
    if (touches.length === 2) return [touches[0]!, touches[1]!];
  }
  return null;
}
